import Box from "@mui/material/Box";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export interface WaterfallStep { key: string; label: string; value: number; kind: "total" | "up" | "down" | "invol" }
export interface WaterfallChartProps {
  steps: WaterfallStep[];
  valueFormatter?: (v: number) => string;
  /** Formato do eixo Y (padrão: `valueFormatter`), ex.: "R$ 200 mil" sem a casa decimal. */
  axisFormatter?: (v: number) => string;
  height?: number;
  width?: number;
  ariaLabel: string;
}

/** Tooltip só com o passo (a barra invisível de base não aparece). Exportado para teste. */
export const waterfallTooltip = (_v: number, _n: string, item: { payload?: { display?: string; label?: string } }) => [item.payload?.display ?? "", item.payload?.label ?? ""];

/** Cores da cascata: total azul, entrada verde, saída voluntária âmbar, saída involuntária vermelha (mockup V4). */
export const WATERFALL_COLORS: Record<WaterfallStep["kind"], string> = { total: "#1f5fbf", up: "#1a7f4b", down: "#b26b00", invol: "#b42318" };

/**
 * Puro: cada passo vira uma barra flutuante. `total` → base 0 e vão = valor (e reinicia o acumulado);
 * `up` → começa no acumulado; `down`/`invol` → começa no acumulado já descontado, vão = |valor|.
 */
export function waterfallBars(steps: WaterfallStep[]): Array<WaterfallStep & { base: number; span: number }> {
  let acc = 0;
  return steps.map((s) => {
    if (s.kind === "total") {
      acc = s.value;
      return { ...s, base: 0, span: s.value };
    }
    if (s.kind === "up") {
      const base = acc;
      acc += s.value;
      return { ...s, base, span: s.value };
    }
    acc += s.value;
    return { ...s, base: acc, span: Math.abs(s.value) };
  });
}

const MINUS = "−";
const NBSP = "\u00A0";
/** Rótulo numa linha só: o recharts quebra `Text` nos espaços comuns ("R$ / 200,0 / mil"), não no NBSP (BUG-GR-V01). */
export const keepTogether = (s: string) => s.replace(/ /g, NBSP);

const TICK_FONT = 11;
const TICK_LINE = 12;
/** Largura média aproximada de um caractere a 11px. */
const CHAR_PX = 6;

/** Quebra o rótulo por palavra em linhas de até `maxChars` caracteres (palavra maior que o limite fica inteira). Exportado para teste. */
export function wrapLabel(label: string, maxChars: number): string[] {
  const lines: string[] = [];
  let cur = "";
  for (const w of label.split(/\s+/).filter(Boolean)) {
    if (!cur) cur = w;
    else if (cur.length + 1 + w.length <= maxChars) cur = `${cur} ${w}`;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

type TickProps = { x?: number; y?: number; width?: number; visibleTicksCount?: number; payload?: { value?: string } };

/** Tick do eixo X na horizontal, quebrado em linhas dentro da largura da barra; o rótulo inteiro fica no `title` (dica). */
function WrappedTick({ x = 0, y = 0, width = 0, visibleTicksCount = 1, payload }: TickProps) {
  const label = String(payload?.value ?? "");
  const band = width / Math.max(1, visibleTicksCount);
  const lines = wrapLabel(label, Math.max(6, Math.floor((band - 4) / CHAR_PX)));
  return (
    <text x={x} y={y + TICK_LINE} textAnchor="middle" fontSize={TICK_FONT} fill="#666">
      <title>{label}</title>
      {lines.map((l, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : TICK_LINE}>
          {l}
        </tspan>
      ))}
    </text>
  );
}

/** Cascata (ponte) de totais e variações: barra invisível de base + barra visível do vão, com rótulo assinado. */
export default function WaterfallChart({ steps, valueFormatter, axisFormatter, height = 260, width, ariaLabel }: WaterfallChartProps) {
  const f = (v: number) => keepTogether(valueFormatter ? valueFormatter(v) : String(v));
  const axis = (v: number) => keepTogether((axisFormatter ?? valueFormatter ?? String)(v));
  const signed = (s: WaterfallStep) => (s.kind === "total" || s.value === 0 ? f(s.value) : s.value < 0 ? `${MINUS}${f(Math.abs(s.value))}` : `+${f(s.value)}`);
  const rows = waterfallBars(steps).map((b) => ({ ...b, display: signed(b) }));
  const chart = (
    <BarChart data={rows} width={width} height={height} margin={{ top: 20, right: 8, bottom: 8, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      {/* Rótulos na horizontal, quebrados em linhas na largura da barra (os inclinados saíam cortados, BUG-GR-V01). */}
      <XAxis dataKey="label" interval={0} tick={<WrappedTick />} height={76} />
      <YAxis width={valueFormatter ? 84 : 48} tick={{ fontSize: TICK_FONT }} tickFormatter={axis} />
      <Tooltip formatter={waterfallTooltip} labelFormatter={() => ""} />
      <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} legendType="none" tooltipType="none" />
      <Bar dataKey="span" stackId="w" isAnimationActive={false}>
        {rows.map((r) => (
          <Cell key={r.key} fill={WATERFALL_COLORS[r.kind]} />
        ))}
        <LabelList dataKey="display" position="top" fontSize={11} />
      </Bar>
    </BarChart>
  );
  return (
    <Box role="img" aria-label={ariaLabel}>
      {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
    </Box>
  );
}
